import { Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AlertCardComponent } from '../../../../../shared/components/alert-card/alert-card.component';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { ConciliacaoBancariaService } from '../../../../../shared/services/apis/financas/conciliacao-bancaria.service';
import { ImportarOfxRequest } from '../../../../../shared/services/requests/financas/importar-ofx.request';
import { ImportarOfxResponse } from '../../../../../shared/services/responses/financas/importar-ofx.response';

export interface ImportarOfxDialogData {
  idContaFinanceira: number;
}

@Component({
  selector: 'app-importar-ofx-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, AlertCardComponent],
  templateUrl: './importar-ofx-dialog.component.html',
  styleUrls: ['./importar-ofx-dialog.component.css'],
})
export class ImportarOfxDialogComponent {
  isDragOver = false;
  isLoading = false;
  selectedFile: File | null = null;
  divergenteResponse: ImportarOfxResponse | null = null;

  constructor(
    public dialogRef: MatDialogRef<ImportarOfxDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImportarOfxDialogData,
    private conciliacaoBancariaService: ConciliacaoBancariaService,
    private alertService: AlertService
  ) {}

  // ── Drag & Drop ──────────────────────────────────────────────

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    const file = event.dataTransfer?.files[0];
    if (file) this.processarArquivo(file);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) this.processarArquivo(file);
  }

  removerArquivo(event: Event): void {
    event.stopPropagation();
    this.selectedFile = null;
    this.divergenteResponse = null;
  }

  private processarArquivo(file: File): void {
    if (!file.name.toLowerCase().endsWith('.ofx')) {
      this.alertService.show('Arquivo inválido', 'Apenas arquivos .ofx são permitidos.', AlertType.warning);
      return;
    }
    this.selectedFile = file;
    this.divergenteResponse = null;
  }

  // ── Import ───────────────────────────────────────────────────

  importar(confirmarDivergente = false): void {
    if (!this.selectedFile || this.isLoading) return;

    this.isLoading = true;
    const reader = new FileReader();
    reader.readAsDataURL(this.selectedFile);

    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      const request: ImportarOfxRequest = {
        idContaFinanceira: this.data.idContaFinanceira,
        arquivoBase64: base64,
        nomeArquivo: this.selectedFile!.name,
        confirmarImportacaoDivergente: confirmarDivergente,
      };

      this.conciliacaoBancariaService.importarOfx(request).subscribe({
        next: res => {
          this.isLoading = false;
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription, AlertType.error);
            return;
          }
          if (res.contaDivergente) {
            this.divergenteResponse = res;
            return;
          }
          this.alertService.show(
            'Importação concluída',
            `${res.totalImportados} lançamento(s) importado(s). ${res.totalDuplicados} já existiam e foram ignorados.`,
            AlertType.success
          );
          this.dialogRef.close(true);
        },
        error: () => {
          this.isLoading = false;
          this.alertService.show('Erro', 'Falha ao importar o arquivo.', AlertType.error);
        },
      });
    };

    reader.onerror = () => {
      this.isLoading = false;
      this.alertService.show('Erro', 'Não foi possível ler o arquivo.', AlertType.error);
    };
  }

  confirmarDivergente(): void {
    this.divergenteResponse = null;
    this.importar(true);
  }

  cancelarDivergente(): void {
    this.selectedFile = null;
    this.divergenteResponse = null;
  }
}
